import type { ReactNode } from 'react'
import type { CSSVars } from '@/types/css'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { CosmicLensGlyph } from '@/components/generals/wordmark'

interface VisitMapProps {
    className?: string
}

const contour = (cx: number, cy: number, r: number, wobble: number, seed: number) => {
    const points: string[] = []

    for (let i = 0; i <= 64; i++) {
        const angle = (i / 64) * Math.PI * 2
        const radius = r * (1 + wobble * Math.sin(angle * 3 + seed) * 0.6 + wobble * Math.cos(angle * 5 - seed * 1.7) * 0.4)

        points.push(`${(cx + Math.cos(angle) * radius * 1.25).toFixed(1)},${(cy + Math.sin(angle) * radius).toFixed(1)}`)
    }

    return `M ${points.join(' L ')} Z`
}

/** A quiet locator map in the observatory's hairline style. */
export const VisitMap = ({ className }: VisitMapProps): ReactNode => {
    const { ref, inView } = useInView<HTMLDivElement>()

    return (
        <div ref={ref} className={cn('relative aspect-[5/4] w-full overflow-hidden border border-hair bg-ink-2', inView && 'is-drawn', className)}>
            <svg viewBox='0 0 500 400' className='absolute inset-0 h-full w-full' role='img' aria-label='Map showing CosmicLens Observatory on Hollowmoor Ridge, north-west of Wheddon Cross, with Dunkery Beacon to the north-west'>
                <g fill='none' strokeWidth={0.8}>
                    {[0, 1, 2, 3, 4, 5, 6].map(step => (
                        <path key={`ridge-${step}`} className='draw' pathLength={1} d={contour(212, 168, 18 + step * 22, 0.12, 1.3)} stroke='var(--c-hair-2)' style={{ '--draw-delay': `${step * 120}ms` } as CSSVars} />
                    ))}
                    {[0, 1, 2, 3].map(step => (
                        <path key={`beacon-${step}`} className='draw' pathLength={1} d={contour(110, 72, 10 + step * 16, 0.1, 4.2)} stroke='var(--c-hair)' style={{ '--draw-delay': `${300 + step * 120}ms` } as CSSVars} />
                    ))}
                </g>

                <path className='draw' pathLength={1} d='M 330 0 C 322 80 340 150 318 210 S 300 330 312 400' fill='none' stroke='var(--c-star-3)' strokeWidth={2} />
                <path className='draw' pathLength={1} d='M 0 236 C 90 228 170 250 240 238 S 300 222 318 226 L 500 252' fill='none' stroke='var(--c-star-3)' strokeWidth={1.4}  />
                <path className='tick' d='M 250 238 C 246 220 236 204 224 186' fill='none' stroke='var(--c-brass)' strokeWidth={1.2} strokeDasharray='3 3' />

                <circle cx={318} cy={226} r={3} fill='var(--c-star-2)' />
                <text x={328} y={248} fill='var(--c-star-2)' fontSize={11} fontFamily='var(--font-app-heading, serif)' fontStyle='italic'>
                    Wheddon Cross
                </text>

                <polygon points='110,62 116,74 104,74' fill='none' stroke='var(--c-star-3)' strokeWidth={1} />
                <text x={122} y={70} fill='var(--c-star-3)' fontSize={10} fontFamily='var(--font-app-mono, monospace)' letterSpacing={1}>
                    DUNKERY BEACON 519 M
                </text>

                <text x={338} y={22} fill='var(--c-star-3)' fontSize={9} fontFamily='var(--font-app-mono, monospace)' letterSpacing={1}>
                    A396 TO MINEHEAD
                </text>
                <text x={340} y={390} fill='var(--c-star-3)' fontSize={9} fontFamily='var(--font-app-mono, monospace)' letterSpacing={1}>
                    A396 TO DULVERTON
                </text>
                <text x={14} y={226} fill='var(--c-star-3)' fontSize={9} fontFamily='var(--font-app-mono, monospace)' letterSpacing={1}>
                    B3224 TO EXFORD
                </text>

                <g transform='translate(452 46)'>
                    <circle r={16} fill='none' stroke='var(--c-hair-2)' strokeWidth={0.8} />
                    <path d='M 0 -13 L 4 3 L 0 0 L -4 3 Z' fill='var(--c-brass)' />
                    <text y={-20} textAnchor='middle' fill='var(--c-star-2)' fontSize={9} fontFamily='var(--font-app-mono, monospace)'>
                        N
                    </text>
                </g>

                <g transform='translate(24 372)'>
                    <line x1={0} y1={0} x2={80} y2={0} stroke='var(--c-star-3)' strokeWidth={1} />
                    <line x1={0} y1={-4} x2={0} y2={4} stroke='var(--c-star-3)' strokeWidth={1} />
                    <line x1={40} y1={-3} x2={40} y2={3} stroke='var(--c-star-3)' strokeWidth={1} />
                    <line x1={80} y1={-4} x2={80} y2={4} stroke='var(--c-star-3)' strokeWidth={1} />
                    <text x={0} y={-9} fill='var(--c-star-3)' fontSize={9} fontFamily='var(--font-app-mono, monospace)'>
                        0
                    </text>
                    <text x={80} y={-9} textAnchor='middle' fill='var(--c-star-3)' fontSize={9} fontFamily='var(--font-app-mono, monospace)'>
                        1 KM
                    </text>
                </g>
            </svg>

            <div className='absolute left-[44.8%] top-[46.5%] -translate-x-1/2 -translate-y-1/2'>
                <span aria-hidden className='slow-pulse absolute left-1/2 top-1/2 size-14 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brass/50' />
                <CosmicLensGlyph className='relative size-8' />
            </div>
            <p className='label-mono absolute left-[44.8%] top-[46.5%] ml-7 mt-4 whitespace-nowrap text-brass'>
                CosmicLens
            </p>
        </div>
    )
}
